import { useState } from "react";
import axios from "axios";
import { RefreshCw, Loader2, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default: first day of the previous month → catches missed invoices for last ~30-60 days
const getDefaultSinceDate = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};

export default function AxonautSyncButton({ onSuccess }) {
  const [open, setOpen] = useState(false);
  const [sinceDate, setSinceDate] = useState(getDefaultSinceDate());
  const [syncing, setSyncing] = useState(false);
  const [replaying, setReplaying] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const token = localStorage.getItem("token");
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const handleSync = async () => {
    if (!sinceDate) {
      toast.error("Sélectionnez une date de départ");
      return;
    }
    setSyncing(true);
    setLastResult(null);
    try {
      const { data } = await axios.post(
        `${API}/webhooks/axonaut/sync-recent`,
        null,
        {
          params: { since_date: sinceDate },
          headers: authHeader,
        }
      );
      toast.success(
        `Synchronisation lancée. Récupération des factures depuis le ${sinceDate}. Ce traitement peut prendre plusieurs minutes.`,
        { duration: 6000 }
      );
      setLastResult({ type: "sync", message: data?.message || "Synchronisation en cours" });
      // Refresh the invoice list after 8 seconds (queue processes ~1 invoice / 3s)
      setTimeout(() => onSuccess?.(), 8000);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`Échec de la synchronisation : ${msg}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleReplay = async () => {
    setReplaying(true);
    setLastResult(null);
    try {
      const { data } = await axios.post(
        `${API}/webhooks/axonaut/resync-from-logs`,
        null,
        {
          params: { limit: 100 },
          headers: authHeader,
        }
      );
      const queuedCount = data?.queued_count ?? 0;
      const skippedExisting = data?.skipped_existing_count ?? 0;
      const logsScanned = data?.logs_scanned ?? 0;

      if (queuedCount > 0) {
        toast.success(
          `${queuedCount} facture(s) manquante(s) remise(s) en file. ${skippedExisting} déjà présente(s) ignorée(s).`,
          { duration: 6000 }
        );
      } else if (skippedExisting > 0) {
        toast.info(
          `Toutes les factures des ${logsScanned} webhook(s) archivé(s) sont déjà présentes dans la base. Rien à rejouer.`,
          { duration: 6000 }
        );
      } else {
        toast.info("Aucun webhook archivé à rejouer.");
      }

      setLastResult({
        type: "replay",
        queued: queuedCount,
        skippedExisting,
        logsScanned,
        invoices: data?.invoices || [],
      });
      if (queuedCount > 0) setTimeout(() => onSuccess?.(), 8000);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`Échec du rejeu : ${msg}`);
    } finally {
      setReplaying(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="flex-1 sm:flex-none border-orange-300 text-orange-700 hover:bg-orange-50 hover:text-orange-800"
          data-testid="axonaut-sync-btn"
          title="Récupérer les factures Axonaut manquantes"
        >
          <RefreshCw className={`w-4 h-4 mr-1 sm:mr-2 ${syncing ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">Sync Axonaut</span>
          <span className="sm:hidden">Sync</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-96 p-4"
        align="end"
        data-testid="axonaut-sync-popover"
      >
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold text-sm mb-1">
              Récupérer les factures Axonaut
            </h4>
            <p className="text-xs text-muted-foreground">
              Utilise l&apos;API Axonaut pour importer les factures manquantes depuis une date donnée.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="since-date" className="text-xs">
              Depuis la date
            </Label>
            <Input
              id="since-date"
              type="date"
              value={sinceDate}
              onChange={(e) => setSinceDate(e.target.value)}
              disabled={syncing}
              max={new Date().toISOString().slice(0, 10)}
              data-testid="axonaut-sync-date"
            />
          </div>

          <Button
            onClick={handleSync}
            disabled={syncing || !sinceDate}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="axonaut-sync-confirm"
          >
            {syncing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Lancement…
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Lancer la synchronisation
              </>
            )}
          </Button>

          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground mb-2">
              Alternative : rejouer les webhooks arrivés récemment mais non traités.
            </p>
            <Button
              onClick={handleReplay}
              disabled={replaying}
              variant="outline"
              size="sm"
              className="w-full"
              data-testid="axonaut-replay-btn"
            >
              {replaying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Rejeu en cours…
                </>
              ) : (
                <>Rejouer les webhooks archivés</>
              )}
            </Button>
          </div>

          {lastResult && lastResult.type === "sync" && (
            <div className="text-xs bg-green-50 border border-green-200 rounded p-2 flex items-start gap-2">
              <Check className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="text-green-800">
                <p className="font-medium">{lastResult.message}</p>
                <p className="text-green-700 mt-1">
                  Le rafraîchissement se fera automatiquement dans quelques secondes.
                </p>
              </div>
            </div>
          )}

          {lastResult && lastResult.type === "replay" && (
            <div className="text-xs bg-blue-50 border border-blue-200 rounded p-2 space-y-1">
              <div className="flex items-center gap-2 text-blue-900 font-medium">
                <Check className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                Résultat du rejeu
              </div>
              <div className="text-blue-800 pl-5 space-y-0.5">
                <p>Webhooks scannés : <span className="font-semibold">{lastResult.logsScanned}</span></p>
                <p>Factures manquantes remises en file : <span className="font-semibold">{lastResult.queued}</span></p>
                <p>Déjà présentes (ignorées) : <span className="font-semibold">{lastResult.skippedExisting}</span></p>
              </div>
            </div>
          )}

          <p className="text-[10px] text-muted-foreground leading-relaxed">
            Le traitement est asynchrone (limité à 1 facture toutes les 3 secondes pour respecter l&apos;API Axonaut). Rafraîchissez la page si nécessaire.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
